import {Draggable, gsap, InertiaPlugin} from "gsap/all";
import seedrandom from "seedrandom"
import $ from "jquery";
import {AnimationSceneSelector} from "./AnimationSceneSelector";
import {AnimationSceneAtom} from "./AnimationSceneAtom";
import {AnimationSceneMolecule} from "./AnimationSceneMolecule";

const range = (start, end, step = 1) => Array.from({length: Math.floor((end - start) / step) + 1}, (_, i) => start + (i * step));

export class AnimationScene {

  constructor({selector, data, allCompletedCallback}) {
    this.$el = $(selector);
    this.currentStep = null;
    this.tl = null;
    this.data = data;
    this.lastChoice = null;
    this.allCompletedCallback = allCompletedCallback;

    this.steps = [];
    this.atoms = [];
    this.molecules = [];

    this.init();
  }

  init() {
    for (let i = 1; i <= 4; i++) {
      this.steps.push({
        stepIndex: i,
        name: `step${i}`,
        goodAnswer: (i === 1) ? "high" : "med",
        optionSelector: new AnimationSceneSelector({
          selector: `#animation-scene-selector-${i}`,
          onChange: this.selectorOnChange.bind(this),
          onConfirm: this.selectorOnConfirm.bind(this),
        })
      });
    }

    // Atoms
    for (let atom of this.data.atoms) {
      const index = this.atoms.length + 1;
      this.atoms.push(new AnimationSceneAtom({
        selector: `#animation-scene-atom-${index}`,
        ...atom
      }));
    }

    // Molecules (atoms groups)
    this.molecules.push(new AnimationSceneMolecule([this.atoms[0], this.atoms[7], this.atoms[8]], {x: 890, y: 610}));
    this.molecules.push(new AnimationSceneMolecule([this.atoms[3], this.atoms[4], this.atoms[9]], {x: 1110, y: 510}));
    this.molecules.push(new AnimationSceneMolecule([this.atoms[11], this.atoms[2], this.atoms[12]], {x: 1410, y: 620}));
    this.molecules.push(new AnimationSceneMolecule([this.atoms[5], this.atoms[13], this.atoms[14]], {x: 1390, y: 410}));
    this.molecules.push(new AnimationSceneMolecule([this.atoms[1], this.atoms[15], this.atoms[17]], {x: 560, y: 500}));
    this.molecules.push(new AnimationSceneMolecule([this.atoms[6], this.atoms[20], this.atoms[16]], {x: 660, y: 280}));
    this.molecules.push(new AnimationSceneMolecule([this.atoms[23], this.atoms[24], this.atoms[10]], {x: 960, y: 250}));
    this.molecules.push(new AnimationSceneMolecule([this.atoms[18], this.atoms[19]], {x: 1690, y: 330}));
    // O+H
    this.molecules.push(new AnimationSceneMolecule([this.atoms[28], this.atoms[29]], {x: 1600, y: 280}));
    this.molecules.push(new AnimationSceneMolecule([this.atoms[30], this.atoms[31]], {x: 1670, y: 600}));
    this.molecules.push(new AnimationSceneMolecule([this.atoms[32], this.atoms[33]], {x: 1220, y: 230}));
    this.molecules.push(new AnimationSceneMolecule([this.atoms[34], this.atoms[35]], {x: 900, y: 410}));
    this.molecules.push(new AnimationSceneMolecule([this.atoms[36], this.atoms[37]], {x: 480, y: 290}));
    this.molecules.push(new AnimationSceneMolecule([this.atoms[38], this.atoms[39]], {x: 1570, y: 180}));
    // Finale molecules
    this.molecules.push(new AnimationSceneMolecule([this.atoms[28], this.atoms[39]], {x: 1600, y: 200}));
    this.molecules.push(new AnimationSceneMolecule([this.atoms[29], this.atoms[38]], {x: 1480, y: 280}));
    this.molecules.push(new AnimationSceneMolecule([this.atoms[21], this.atoms[22]], {x: 480, y: 710}));
    this.molecules.push(new AnimationSceneMolecule([this.atoms[25], this.atoms[26], this.atoms[27]], {x: 1340, y: 730}));

    // Smoke audio (only for middle chimney, since it is always on
    const lottie = this.$el.find(`#smoke-3 .lottie-animation`).data('lottie');
    lottie.addEventListener('loop', ({loopCount}) => {
      app.audio.play("audio/MNS2_Int1.2 Poussiere cosmique_SFX/MNS2_ME_INT1.2_SFXgaz.mp3", 1, true);
    });
    lottie.addEventListener('play', () => {
      app.audio.play("audio/MNS2_Int1.2 Poussiere cosmique_SFX/MNS2_ME_INT1.2_SFXgaz.mp3", 1, true);
    });

  }

  selectorOnChange(choice) {
    console.log("selectorOnChange", this.currentStep.name, choice)

    app.audio.play("audio/MNS2_Int2.2 Invisibles_SFX/MNS2_ME_INT2.2_SFXADNup.mp3", 1, true)

    if (this.tl) {
      this.tl.kill();
      this.tl = null;
    }
    this.tl = gsap.timeline();

    if (this.repeatableTl) {
      this.repeatableTl.kill();
      this.repeatableTl = null;
    }

    if (this.currentStep.name === "step1") {
      const addDelay = (this.lastChoice === "high")
      if (choice === "low") {
        // Wiggle
        for (let atom of this.atoms) atom.setWiggle(1);
        // Smoke
        this.setSmoke(1)
        // Born atoms
        this.tl.add(() => this.atomSetVisibility(range(7, 9), true), (addDelay ? 0.5 : 0))
        this.atomSetVisibility(range(10, 14), false)
        // Bind atoms to molecules
        this.moleculesUnbind([0, 1, 2])
      } else if (choice === "med") {
        // Wiggle
        for (let atom of this.atoms) atom.setWiggle(1);
        // Smoke
        this.setSmoke(3)
        // Bind atoms to molecules
        this.moleculesUnbind([0, 1, 2])
        // Born atoms
        this.tl.add(() => this.atomSetVisibility(range(7, 11), true), (addDelay ? 0.5 : 0))
        this.atomSetVisibility(range(12, 14), false)
      } else if (choice === "high") {
        // Wiggle
        for (let atom of this.atoms) atom.setWiggle(1);
        // Smoke
        this.setSmoke(6)
        // Born atoms
        this.tl.add(() => this.atomSetVisibility(range(7, 14), true), 0)
        // Bind atoms to molecules
        this.tl.add(() => this.moleculesBind([0, 1, 2]), 0.5)
      }

    } else if (this.currentStep.name === "step2") {
      if (choice === "low") {
        // Wiggle
        for (let atom of this.atoms) atom.setWiggle(0);
        // Bind atoms to molecules
        this.moleculesBind([0, 1, 2])
        this.moleculesUnbind([3, 4, 5])
      } else if (choice === "med") {
        // Wiggle
        for (let atom of this.atoms) atom.setWiggle(1);
        // Bind atoms to molecules
        this.moleculesBind([0, 1, 2, 3, 4, 5])
      } else if (choice === "high") {
        // Wiggle
        for (let atom of this.atoms) atom.setWiggle(1.25);

        // Create repeatable loop
        this.repeatableTl = gsap.timeline({repeat: -1});
        // Bind atoms to molecules
        this.repeatableTl.add(() => this.moleculesBind([0, 1, 2, 3, 4, 5]));
        // Then unbind
        this.repeatableTl.add(() => this.moleculesUnbind([0, 1, 2, 3, 4, 5]), 2)
        this.repeatableTl.add(() => {
        }, 4)
      }

    } else if (this.currentStep.name === "step3") {
      if (choice === "low") {
        const addDelay = (this.lastChoice === "high")
        // Molecules unbind
        this.tl.add(() => this.moleculesUnbind([6, 7]));
        //  Show O+H
        this.tl.add(() => this.atomSetVisibility(range(30, 37), true), (addDelay ? 0.50 : 0));
        this.tl.add(() => this.moleculesBind([8, 9, 10, 11, 12]), 0.50);
        this.atomSetVisibility(range(40, 59), false)
      } else if (choice === "med") {
        this.moleculesBind([6, 7])
        //  Hide O+H
        this.atomSetVisibility(range(28, 37), false)
        //  Hide lot of H
        this.atomSetVisibility(range(40, 59), false)
      } else if (choice === "high") {
        const addDelay = (this.lastChoice === "low")
        // Molecules unbind
        this.tl.add(() => this.moleculesUnbind([6, 7]));
        //  Hide O+H
        this.tl.add(() => this.atomSetVisibility(range(28, 37), false));
        //  Reveal lot of H (environ 18)
        this.tl.add(() => this.atomSetVisibility(range(40, 59), true, true), (addDelay ? 0.50 : 0));
      }

    } else if (this.currentStep.name === "step4") {
      if (choice === "low") {
        // Unbind final single elements
        this.moleculesUnbind([14, 15, 16, 17]);
        // Rebind molecules unbounded by level high
        this.moleculesBind([0, 1, 2, 3, 4, 5, 6, 7]);
        // Thunders
        this.setThunder(1)
      } else if (choice === "med") {
        // Bind final single elements
        this.moleculesBind([14, 15, 16, 17]);
        // Rebind molecules unbounded by level high
        this.tl.add(() => this.moleculesBind([0, 1, 2, 3, 4, 5, 6, 7]));
        // Build first complex molecule
        this.tl.add(() => this.buildComplexMolecule([0, 4, 16], 0), 1.5);
        this.tl.add(() => this.buildComplexMolecule([1, 3, 17], 1), 1.75);
        this.tl.add(() => this.buildComplexMolecule([2, 7], 2), 2.0);
        // Gather all in 3 complex molecule
        this.tl.add(() => this.buildComplexMolecule([0, 4, 16, 5], 0), 3.0);
        this.tl.add(() => this.buildComplexMolecule([1, 3, 17, 6], 1), 3.5);
        this.tl.add(() => this.buildComplexMolecule([2, 7, 9, 14, 15], 2), 4.0);
        // Thunders
        this.setThunder(3)
      } else if (choice === "high") {
        // Unbind final single elements
        this.moleculesUnbind([14, 15, 16, 17]);
        // Thunders
        this.setThunder(5)
        this.repeatableTl.add(() => this.moleculesUnbind([0, 4, 5, 6]), 0.0)
        this.repeatableTl.add(() => this.moleculesUnbind([1, 2, 3, 7]), 1.25)
        this.repeatableTl.add(() => this.moleculesBind([0, 1, 2, 3, 4, 5, 6, 7]), 4)
      }
    }

    this.lastChoice = choice;
  }

  goToStep(stepIndex) {

    if (this.currentStep?.stepIndex === stepIndex) {
      console.log("same step skip.");
      return;
    }

    if (stepIndex === 5) {
      this.playFinale()
      return;
    }

    if (this.tl) {
      this.tl.kill();
      this.tl = null;
    }

    this.currentStep = this.steps[stepIndex - 1];

    this.tl = gsap.timeline();
    this.tl.to(this.$el.find(`[data-step][data-step!='${stepIndex}']`), {
      autoAlpha: 0,
      x: -10,
    });
    this.tl.fromTo(this.$el.find(`[data-step='${stepIndex}']`), {
      x: -10
    }, {
      autoAlpha: 1,
      x: 0,
    });

    if (stepIndex >= 1) {
      this.tl.add(() => {
        for (let atom of this.atoms) atom.setWiggle(1);

      })
    }

    if (stepIndex >= 2) {
      this.tl.add(() => {
        this.setSmoke(0)
        this.moleculesBind([0, 1, 2])
        this.atomSetVisibility(range(7, 21), true)
      })
    }

    if (stepIndex >= 3) {
      this.tl.add(() => {
        this.moleculesBind([3, 4, 5])
        this.atomSetVisibility(range(22, 29), true)
        this.atomSetVisibility(range(38, 39), true)
      })
    }

    if (stepIndex >= 4) {
      this.moleculesBind([0, 1, 2, 3, 4, 5, 6, 7]);
    }
  }

  setSmoke(count) {
    function setPlayback(index, play) {
      const $el = $(`#smoke-${index} .lottie-animation`);
      const lottie = $el.data('lottie');
      if (play) {
        gsap.to($el, {autoAlpha: 1})
        lottie.play();
      } else {
        gsap.to($el, {
          autoAlpha: 0, onComplete: () => {
            lottie.stop();
            lottie.setFrame(0)
          }
        })
      }
    }

    setPlayback(3, (count >= 1));
    setPlayback(1, (count >= 2));
    setPlayback(5, (count >= 3));
    setPlayback(2, (count >= 4));
    setPlayback(4, (count >= 5));
    setPlayback(6, (count >= 6));
  }

  setThunder(count) {
    function setPlayback(index, play) {
      const $el = $(`#thunder-${index} .lottie-animation`);
      const lottie = $el.data('lottie');
      if (play) {
        const zebra = (index % 2) + 1;
        app.audio.play(`audio/MNS2_Int1.2 Poussiere cosmique_SFX/MNS2_ME_INT1.2_SFXeclair${zebra}.mp3`, 1, true);
        gsap.set($el, {autoAlpha: 1})
        lottie.play();
      } else {
        gsap.to($el, {
          autoAlpha: 0, onComplete: () => {
            lottie.stop();
            lottie.setFrame(0)
          }
        })
      }
    }

    const highDelay = (count === 5 ? 0.75 : 0);
    const medDelay = (count === 3 ? 3 : 1);

    if (count < 1) setPlayback(1, false);
    if (count < 2) setPlayback(2, false);
    if (count < 3) setPlayback(3, false);
    if (count < 4) setPlayback(4, false);
    if (count < 5) setPlayback(5, false);

    if (this.repeatableTl) {
      this.repeatableTl.kill();
      this.repeatableTl = null;
    }
    this.repeatableTl = gsap.timeline({repeat: -1});
    if (count >= 1) this.repeatableTl.add(() => setPlayback(1, true));
    if (count >= 2) this.repeatableTl.add(() => setPlayback(2, true), 0.5 * medDelay + highDelay);
    if (count >= 3) this.repeatableTl.add(() => setPlayback(3, true), 1.0 * medDelay + highDelay);
    if (count >= 4) this.repeatableTl.add(() => setPlayback(4, true), 1.5 * medDelay + highDelay);
    if (count >= 5) this.repeatableTl.add(() => setPlayback(5, true), 2.0 * medDelay + highDelay);
    this.repeatableTl.add(() => {
    }, 6)

  }

  selectorOnConfirm(choice) {
    console.log("selectorOnConfirm", choice, this.currentStep.goodAnswer)

    app.audio.play("audio/MNS2_Communs_SFX/MNS2_ME_INT_SFXstart.mp3", 1, true)

    if (this.currentStep.goodAnswer === choice) {
      // Good answer
      console.log("good Answer");
      app.audio.play("audio/MNS2_Int1.2 Poussiere cosmique_SFX/MNS2_ME_INT1.2_SFXH2Ogood.mp3", 1, true)
      app.toggleTooltip(true, `level3_step${this.currentStep.stepIndex}_success`)
    } else {
      // Wrong answer
      console.log("wrong answer");
      app.audio.play("audio/MNS2_Int1.2 Poussiere cosmique_SFX/MNS2_ME_INT1.2_SFXH2Obad.mp3", 1, true)
      app.toggleTooltip(true, `level3_step${this.currentStep.stepIndex}_fail`)
    }
  }

  atomSetVisibility(indexes, value, staggering = false) {
    let stagger = 0
    for (const index of indexes) {
      if (value) this.atoms[index].animateIn(stagger)
      else this.atoms[index].animateOut()
      stagger += 0.01;
    }
  }

  moleculesBind(indexes) {
    for (const index of indexes) this.molecules[index].bind()
  }

  moleculesUnbind(indexes) {
    for (const index of indexes) this.molecules[index].unbind()
  }

  buildComplexMolecule(indexes, spotIndex) {
    for (const [i, index] of indexes.entries()) this.molecules[index].buildComplex(spotIndex, i)
  }

  async playFinale() {

    // Stop thunder
    this.setThunder(0)

    // Show additional tooltip (AWAIT)
    await app.toggleTooltip(true, "level3_step5_a")

    if (this.tl) {
      this.tl.kill();
      this.tl = null;
    }
    this.tl = gsap.timeline();

    // Hide all step related elements (used to hide UI)
    this.tl.to(this.$el.find(`[data-step]`), {autoAlpha: 0, x: -10});

    // Hide all molecule
    this.tl.add(() => this.atomSetVisibility(range(0, 40)));

    // Show flare
    this.tl.add(() => {
      const $el = $(`#animation-scene-flare .lottie-animation`);
      const lottie = $el.data('lottie');
      gsap.set($el.parent(), {autoAlpha: 1})
      lottie.addEventListener("complete", () => {
        lottie.removeEventListener("complete");
        gsap.set($el.parent(), {autoAlpha: 0});
      });
      lottie.setFrame(0)
      lottie.play();
    }, 1.25);

    // Flare sound effect
    this.tl.add(() => app.audio.play("audio/MNS2_Int1.2 Poussiere cosmique_SFX/MNS2_ME_INT1.2_SFXreveal.mp3", 1, true), "-=0.1")

    // Show cells
    const cellsBaseOptions = {
      ease: "quad.inOut",
      duration: 1.75,
      autoAlpha: 1,
      scale: 1,
    }
    this.tl.fromTo(this.$el.find(`#cell-1`), {scale: 0, x: 1200, y: 540, autoAlpha: 0.6}, {...cellsBaseOptions, x: 1210, y: 422}, 1.3)
    this.tl.fromTo(this.$el.find(`#cell-2`), {scale: 0, x: 1200, y: 540, autoAlpha: 0.6}, {...cellsBaseOptions, x: 860, y: 250}, 1.4)
    this.tl.fromTo(this.$el.find(`#cell-3`), {scale: 0, x: 1200, y: 540, autoAlpha: 0.6}, {...cellsBaseOptions, x: 1510, y: 185}, 1.5)
    this.tl.fromTo(this.$el.find(`#cell-4`), {scale: 0, x: 1200, y: 540, autoAlpha: 0.6}, {...cellsBaseOptions, x: 1730, y: 360}, 1.6)

    //  Show popup
    if (this.allCompletedCallback) this.tl.add(() => this.allCompletedCallback(), 4.5);

  }

  animateIn() {
    this.tl = gsap.timeline();

    // Debug
    // this.tl.timeScale(1000)

    // Smoke
    gsap.set(this.$el.find(`.smoke .lottie-animation`), {autoAlpha: 0});

    // Show bubbles
    this.tl.add(() => {
      const $el = $(`#animation-scene-transition-bubbles .lottie-animation`);
      const lottie = $el.data('lottie');
      lottie.setFrame(0)
      lottie.play();
    }, 0);

    // Move down
    this.tl.fromTo(this.$el.find(`#animation-scene-landscape`), {
      y: 980
    }, {
      y: 0,
      duration: 2,
      ease: "sine.out"
    }, 0.2);

    // Bring atoms in later
    this.tl.fromTo("#animation-scene-atoms", {autoAlpha: 0}, {autoAlpha: 1});

    this.tl.add(() => {
      this.goToStep(1);
      gsap.set("#level-3-transition-overlay", {autoAlpha: 0})
      for (let atom of this.atoms) {
        atom.reset();
      }
    })

    return this;
  }

  animateOut() {
    this.tl = gsap.timeline();

    this.tl.to(this.$el.find(`#animation-scene-landscape, #animation-scene-cells`), {
      y: 980,
      duration: 2,
      ease: "sine.in"
    });

    // Show bubbles
    this.tl.add(() => {
      const $el = $(`#animation-scene-transition-bubbles .lottie-animation`);
      const lottie = $el.data('lottie');
      lottie.setFrame(0)
      lottie.play();
    }, 0.5);

    // Add transition overlay
    this.tl.add(() => gsap.set("#level-3-transition-overlay", {autoAlpha: 1}));

    return this.tl;
  }

  enable() {

  }

  disable() {
    if (this.tl) {
      this.tl.kill();
      this.tl = null;
    }

    if (this.repeatableTl) {
      this.repeatableTl.kill();
      this.repeatableTl = null;
    }

    return this;
  }

  reset() {

    this.disable();

    for (let step of this.steps) {
      step.optionSelector.reset();
    }

    for (let atom of this.atoms) {
      atom.reset();
    }

    for (let molecule of this.molecules) {
      molecule.reset();
    }

    this.setSmoke(0)
    this.setThunder(0)

    gsap.set(this.$el.find(`[data-step]`), {autoAlpha: 0});
    gsap.set("#animation-scene-atoms", {autoAlpha: 0});
    gsap.set(this.$el.find(`.thunder .lottie-animation`), {autoAlpha: 0});
    gsap.set("#animation-scene-flare", {autoAlpha: 0});
    gsap.set("#animation-scene-cells .cell", {autoAlpha: 0});
    gsap.set("#animation-scene-cells", {y: 0});

    this.lastChoice = null;
    this.currentStep = null;

    return this;
  }
}

